// Bottom sheet: "Plant a seed". One primary way in — drop your stems, from
// any DAW (/upload/files). Then straight from Ableton (extension install /
// email flow) or Audiotool (its import). Starting from nothing — a blank room
// in the browser (/room/new), plus any drafts this browser is keeping — is a
// quiet link at the bottom: Seedbed leads with planting work you've started.

import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
// useNavigate used by AudiotoolStep
import { useIsNarrow } from "@web-shared/hooks/useIsNarrow";
import { audiotoolUploadPath, draftRoomPath, filesUploadPath, newRoomPath } from "@/lib/ctas";
import { listDrafts, type RoomDraft } from "@/lib/room-drafts";
import { webApi, json } from "@web-shared/lib/web-api";
import { useAuthGate } from "../auth";
import { useSession } from "../session";

type Step = "pick" | "ableton" | "audiotool" | "other";

export function UploadPicker({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<Step>("pick");

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/40" onClick={onClose} />
      <div className="fixed inset-x-0 bottom-0 z-50 rounded-t-3xl bg-raised border-t border-white/10 px-5 pt-3 pb-[calc(4rem+env(safe-area-inset-bottom))] shadow-[0_-20px_40px_rgba(0,0,0,0.6)] max-h-[85dvh] overflow-y-auto overscroll-contain">
        <div className="w-10 h-1 rounded-full bg-white/20 mx-auto" />

        {step === "pick" && <PickStep onPick={setStep} onClose={onClose} />}
        {step === "ableton" && <AbletonStep onBack={() => setStep("pick")} />}
        {step === "audiotool" && (
          <AudiotoolStep onClose={onClose} onBack={() => setStep("pick")} />
        )}
        {step === "other" && <OtherStep onBack={() => setStep("pick")} />}
      </div>
    </>
  );
}

function PickStep({ onPick, onClose }: { onPick: (step: Step) => void; onClose: () => void }) {
  const navigate = useNavigate();
  const narrow = useIsNarrow();
  // Seeds started here and not yet saved, newest first.
  const [drafts, setDrafts] = useState<RoomDraft[]>([]);
  useEffect(() => {
    let live = true;
    void listDrafts().then((d) => {
      if (live) setDrafts(d.slice(0, 3));
    });
    return () => {
      live = false;
    };
  }, []);
  const go = (path: string) => {
    onClose();
    navigate(path);
  };
  return (
    <>
      <h3 className="mt-4 text-2xl font-black tracking-tight">Plant a seed</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Got a track started? Plant it and anyone can remix it.
      </p>
      {/* One obvious way in: the stems, from wherever they were made. */}
      <button
        type="button"
        onClick={() => go(filesUploadPath())}
        className="mt-4 w-full p-4 rounded-2xl border-2 border-primary bg-primary/10 flex items-center gap-3 text-left shadow-glow"
      >
        <span className="size-12 rounded-xl bg-primary text-primary-foreground flex items-center justify-center shrink-0">
          <svg className="size-6" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M12 15V4M7 9l5-5 5 5M5 15v4h14v-4" /></svg>
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-base font-bold">Drop your stems</span>
          <span className="block text-xs text-muted-foreground">
            {narrow ? "Stems, a bounce or a voice memo" : "From any DAW: a stems folder, a zip or a bounce"}
          </span>
        </span>
      </button>
      <p className="mt-4 text-xs font-semibold text-muted-foreground">Or plant straight from</p>
      <div className="mt-2 flex flex-col gap-2">
        <DawOption
          name="Ableton Live 12"
          sub="With the Seedbed extension"
          icon={<img src="/ableton-icon.webp" alt="" className="h-4 w-auto rounded invert" />}
          onClick={() => onPick("ableton")}
        />
        <DawOption
          name="Audiotool"
          sub="Pick one of your projects"
          icon={<img src="/audiotool-icon.webp" alt="" className="size-6 rounded" />}
          onClick={() => onPick("audiotool")}
        />
      </div>
      {/* Starting from nothing is allowed, not the lead: a quiet link, with
          the drafts this browser keeps from earlier blank starts. */}
      <div className="mt-5 flex flex-col items-start gap-1.5 text-sm">
        {drafts.map((d) => (
          <button
            key={d.id}
            type="button"
            onClick={() => go(draftRoomPath(d.id))}
            className="text-muted-foreground hover:text-foreground"
          >
            Continue <span className="text-foreground">“{d.title.trim() || "Untitled seed"}”</span>
            <span className="text-xs"> · draft, {d.room.tracks.length} track{d.room.tracks.length === 1 ? "" : "s"}</span>
          </button>
        ))}
        <button type="button" onClick={() => go(newRoomPath())} className="text-muted-foreground hover:text-foreground">
          No track yet? <span className="underline underline-offset-2">Start a blank seed here</span>
        </button>
      </div>
    </>
  );
}

function DawOption({
  name,
  sub,
  icon,
  onClick,
}: {
  name: string;
  sub: string;
  icon: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full p-3 rounded-2xl border border-white/10 bg-white/[0.03] flex items-center gap-3 text-left"
    >
      <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 overflow-hidden bg-[#0d0d12] border border-white/10">
        {icon}
      </span>
      <span className="flex-1 min-w-0">
        <span className="block font-semibold">{name}</span>
        <span className="block text-xs text-muted-foreground">{sub}</span>
      </span>
      <svg className="size-4 shrink-0 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M9 6l6 6-6 6" /></svg>
    </button>
  );
}

function AbletonStep({ onBack }: { onBack: () => void }) {
  const [sub, setSub] = useState<"ask" | "has-ext" | "no-ext" | "no-ableton">("ask");

  return (
    <>
      <div className="mt-4 flex items-center gap-3">
        <div className="size-11 rounded-xl bg-[#0d0d12] border border-white/10 flex items-center justify-center shrink-0">
          <img src="/ableton-icon.webp" alt="" className="h-4 w-auto invert" />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-bold">Plant from Ableton</h3>
          <p className="text-[11px] text-muted-foreground">Ableton Live 12 Suite</p>
        </div>
      </div>

      {sub === "ask" && (
        <>
          <p className="mt-4 text-sm font-semibold">Do you have the Seedbed extension?</p>
          <p className="mt-1 text-xs text-muted-foreground">
            The extension uploads stems directly from your Live Set. Right-click anywhere in your session, then Upload Seed.
          </p>
          <div className="mt-3 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setSub("has-ext")}
              className="h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center"
            >
              Yes, I have it
            </button>
            <button type="button" onClick={() => setSub("no-ext")} className="h-11 rounded-xl border border-white/15 text-sm font-semibold flex items-center justify-center">
              No, I need the extension
            </button>
          </div>
        </>
      )}

      {sub === "has-ext" && (
        <div className="mt-4 flex flex-col items-center text-center gap-3">
          <div className="size-12 rounded-full bg-primary/15 flex items-center justify-center">
            <svg className="size-6 text-primary" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M20 6L9 17l-5-5" /></svg>
          </div>
          <div>
            <h3 className="text-base font-bold">You're all set</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              Right-click anywhere in your Live Set and choose <span className="text-foreground font-medium">Upload Seed</span>. Your stems will land here automatically.
            </p>
          </div>
        </div>
      )}

      {sub === "no-ext" && (
        <>
          <p className="mt-4 text-sm font-semibold">Do you have Ableton Live 12?</p>
          <p className="mt-1 text-xs text-muted-foreground">The extension requires Live 12 Suite.</p>
          <div className="mt-3 flex flex-col gap-2">
            <a
              href="https://seed-bed.com/download"
              target="_blank"
              rel="noopener noreferrer"
              className="h-11 rounded-xl bg-primary text-primary-foreground font-bold text-sm flex items-center justify-center"
            >
              Yes, get the extension
            </a>
            <button type="button" onClick={() => setSub("no-ableton")} className="h-11 rounded-xl border border-white/15 text-sm font-semibold flex items-center justify-center">
              No
            </button>
          </div>
        </>
      )}

      {sub === "no-ableton" && (
        <div className="mt-4 text-center">
          <h3 className="text-base font-bold">Ableton Live 12 required</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Uploading from Ableton requires Live 12 Suite. You can plant a seed from Audiotool instead — it's free and runs in any browser.
          </p>
        </div>
      )}

      <button type="button" onClick={sub === "ask" ? onBack : () => setSub("ask")} className="mt-3 text-sm text-muted-foreground">
        ← Back
      </button>
    </>
  );
}

function AudiotoolStep({ onClose, onBack }: { onClose: () => void; onBack: () => void }) {
  const navigate = useNavigate();
  const session = useSession();
  const gate = useAuthGate();

  return (
    <>
      <div className="mt-4 flex items-center gap-3">
        <div className="size-11 rounded-xl bg-[#0d0d12] border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
          <img src="/audiotool-icon.webp" alt="" className="size-7 rounded" />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-bold">Plant from Audiotool</h3>
          <p className="text-[11px] text-muted-foreground">Free, runs in any browser</p>
        </div>
      </div>
      <p className="mt-3 text-sm text-muted-foreground">
        Pick one of your Audiotool projects. Seedbed will render its tracks and plant them as a seed — stems, tempo and all.
      </p>
      <div className="mt-4 flex flex-col gap-2">
        {session ? (
          <button
            type="button"
            onClick={() => { onClose(); navigate(audiotoolUploadPath()); }}
            className="h-11 rounded-xl bg-[#7c5cbf] text-white font-bold text-sm flex items-center justify-center gap-2"
          >
            Choose a project
            <svg className="size-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24"><path d="M7 17L17 7M9 7h8v8" /></svg>
          </button>
        ) : (
          <button
            type="button"
            onClick={() =>
              gate.requireSignIn("audiotool_upload", "Sign in to plant from Audiotool", () => {
                onClose();
                navigate(audiotoolUploadPath());
              })
            }
            className="h-11 rounded-xl bg-[#7c5cbf] text-white font-bold text-sm flex items-center justify-center"
          >
            Sign in to plant
          </button>
        )}
      </div>
      <button type="button" onClick={onBack} className="mt-3 text-sm text-muted-foreground">
        ← Back
      </button>
    </>
  );
}

const DAWS = ["FL Studio", "Logic Pro", "Bitwig", "Pro Tools", "Reaper", "GarageBand", "Other"] as const;

function OtherStep({ onBack }: { onBack: () => void }) {
  const [selected, setSelected] = useState("FL Studio");
  const [customDaw, setCustomDaw] = useState("");
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const dawValue = selected === "Other" ? customDaw.trim() : selected;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !dawValue) return;
    setSending(true);
    try {
      await webApi("/waitlist", json("POST", { email, daw: dawValue }));
    } catch { /* best effort */ }
    setSending(false);
    setSent(true);
  };

  return (
    <>
      <h3 className="mt-4 text-2xl font-black tracking-tight">Not in your DAW yet</h3>
      <p className="mt-1.5 text-sm text-muted-foreground">
        Right now you can plant seeds from Ableton Live and Audiotool. Tell us what you use and we'll let you know when it's supported.
      </p>
      {sent ? (
        <div className="mt-4 rounded-2xl bg-primary/10 border border-primary/30 p-4 text-center">
          <div className="text-sm font-semibold text-primary">We'll let you know when {dawValue} is supported.</div>
        </div>
      ) : (
        <form onSubmit={(e) => void submit(e)} className="mt-4 flex flex-col gap-3">
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="h-12 w-full px-4 rounded-2xl bg-[#0d0d12] border border-white/15 text-sm text-foreground appearance-none"
          >
            {DAWS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          {selected === "Other" && (
            <input
              type="text"
              placeholder="Which DAW?"
              value={customDaw}
              onChange={(e) => setCustomDaw(e.target.value)}
              className="h-12 w-full px-4 rounded-2xl bg-[#0d0d12] border border-white/15 text-sm placeholder-muted-foreground/50"
              required
              maxLength={100}
            />
          )}
          <div className="flex gap-2">
            <input
              type="email"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 min-w-0 h-12 px-4 rounded-2xl bg-[#0d0d12] border border-white/15 text-sm placeholder-muted-foreground/50"
              required
            />
            <button
              type="submit"
              disabled={sending || !dawValue}
              className="h-12 px-4 rounded-2xl bg-white text-black font-bold text-sm whitespace-nowrap disabled:opacity-50"
            >
              {sending ? "..." : "Notify me"}
            </button>
          </div>
        </form>
      )}
      <button type="button" onClick={onBack} className="mt-3 text-sm text-muted-foreground">
        ← Back
      </button>
    </>
  );
}
